import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Button,
    TextField,
    IconButton,
    Chip,
    Tooltip,
    Modal,
    CircularProgress,
    Stack,
    InputAdornment,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Paper
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Visibility as ViewIcon,
    Search as SearchIcon,
    Discount as DiscountIcon,
    ReceiptLong as TariffIcon
} from '@mui/icons-material';
import { useApiHandler } from '../../../hooks/useApiHandler';
import { customerService } from '../../../services/customerService';
import TariffDialog from '../../../components/tariff/TariffDialog';
import CustomerForm from './CustomerForm';

const CustomerList = ({ onDataChange }) => {
    const { handleApiCall, showSuccess, showError } = useApiHandler();
    const [loading, setLoading] = useState(false);
    const [customers, setCustomers] = useState([]);
    const [searchText, setSearchText] = useState('');
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingCustomer, setEditingCustomer] = useState(null);
    const [viewMode, setViewMode] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [customerToDelete, setCustomerToDelete] = useState(null);
    const [tariffCustomer, setTariffCustomer] = useState(null);
    const [pagination, setPagination] = useState({
        current_page: 1,
        per_page: 10,
        total: 0,
        last_page: 1,
        from: 0,
        to: 0
    });

    // Latest request wins: a slow response to an older search must not
    // overwrite the results of a newer one.
    const requestSeq = useRef(0);

    // One fetch per change of page, page size or (debounced) search text
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            fetchCustomers(searchText);
        }, searchText ? 300 : 0);

        return () => clearTimeout(timeoutId);
    }, [page, rowsPerPage, searchText]);

    const fetchCustomers = async (search = searchText) => {
        const seq = ++requestSeq.current;
        setLoading(true);
        try {
            const params = {
                search,
                page: page + 1, // Convert 0-based to 1-based
                per_page: rowsPerPage
            };

            const response = await customerService.getAllCustomers(params);
            if (seq !== requestSeq.current) return;
            if (response.success) {
                setCustomers(response.data);
                if (response.pagination) {
                    setPagination(response.pagination);
                }
            }
        } catch (error) {
            if (seq !== requestSeq.current) return;
            console.error('Error fetching customers:', error);
            showError('Failed to fetch customers. Please try again.');
        } finally {
            if (seq === requestSeq.current) setLoading(false);
        }
    };

    // Stats only change when a customer is added, edited or removed
    const refreshAfterChange = () => {
        fetchCustomers();
        if (onDataChange) {
            onDataChange();
        }
    };

    const handleSearch = (value) => {
        setSearchText(value);
        setPage(0);
        // fetchCustomers will be called by useEffect when searchText changes
    };

    const handleAdd = () => {
        setEditingCustomer(null);
        setViewMode(false);
        setIsModalVisible(true);
    };

    const handleEdit = (customer) => {
        setEditingCustomer(customer);
        setViewMode(false);
        setIsModalVisible(true);
    };

    const handleView = (customer) => {
        setEditingCustomer(customer);
        setViewMode(true);
        setIsModalVisible(true);
    };

    const handleDeleteClick = (customer) => {
        setCustomerToDelete(customer);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (customerToDelete) {
            try {
                const response = await customerService.deleteCustomer(customerToDelete.id);
                if (response.success) {
                    showSuccess('Customer deleted successfully');
                    refreshAfterChange();
                } else {
                    showError(response.message || 'Failed to delete customer');
                }
            } catch (error) {
                showError(error.message || 'Failed to delete customer. Please try again.');
            }
        }
        setDeleteDialogOpen(false);
        setCustomerToDelete(null);
    };

    const handleModalClose = () => {
        setIsModalVisible(false);
        setEditingCustomer(null);
        setViewMode(false);
    };

    const handleFormSuccess = () => {
        setIsModalVisible(false);
        setEditingCustomer(null);
        setViewMode(false);
        refreshAfterChange();
        const action = editingCustomer ? 'updated' : 'created';
        showSuccess(`Customer ${action} successfully`);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Server-side pagination - no need for client-side filtering

    return (
        <Box sx={{ p: 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                <TextField
                    placeholder="Search customers..."
                    value={searchText}
                    onChange={(e) => handleSearch(e.target.value)}
                    size="small"
                    sx={{ width: 300 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                    }}
                />
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleAdd}
                >
                    Add New Customer
                </Button>
            </Stack>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>S.No</TableCell>
                            <TableCell>Code</TableCell>
                            <TableCell>Name</TableCell>
                            <TableCell>Company</TableCell>
                            <TableCell>Email</TableCell>
                            <TableCell>Mobile</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Packages</TableCell>
                            <TableCell>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={9} align="center" sx={{ py: 3 }}>
                                    <CircularProgress />
                                </TableCell>
                            </TableRow>
                        ) : customers.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={9} align="center" sx={{ py: 3 }}>
                                    <Typography color="text.secondary">
                                        No customers found
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            customers.map((customer, index) => (
                                <TableRow key={customer.id}>
                                    <TableCell>{(pagination.current_page - 1) * pagination.per_page + index + 1}</TableCell>
                                    <TableCell>{customer.cust_code}</TableCell>
                                    <TableCell>{customer.cust_name}</TableCell>
                                    <TableCell>{customer.from_company}</TableCell>
                                    <TableCell>{customer.from_email}</TableCell>
                                    <TableCell>{customer.from_mobile_no}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={customer.cust_acc_status === 'Y' ? 'Active' : 'Inactive'}
                                            color={customer.cust_acc_status === 'Y' ? 'success' : 'error'}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        {/* An assignment restricts what the customer
                                            can book, so "none" means unrestricted,
                                            not a missing setting. Say which it is. */}
                                        {customer.discounts?.length > 0 ? (
                                            <Tooltip title="Only these packages are offered when booking for this customer">
                                                <Chip
                                                    icon={<DiscountIcon />}
                                                    label={`${customer.discounts.length} assigned`}
                                                    color="info"
                                                    size="small"
                                                />
                                            </Tooltip>
                                        ) : (
                                            <Tooltip title="No packages assigned. This customer can book any package priced for the route">
                                                <Chip
                                                    label="All packages"
                                                    variant="outlined"
                                                    size="small"
                                                />
                                            </Tooltip>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Stack direction="row" spacing={1}>
                                            <Tooltip title="View tariff for this customer's packages">
                                                <IconButton
                                                    size="small"
                                                    color="primary"
                                                    onClick={() => setTariffCustomer(customer)}
                                                >
                                                    <TariffIcon />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="View Details">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleView(customer)}
                                                >
                                                    <ViewIcon />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Edit Customer">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleEdit(customer)}
                                                >
                                                    <EditIcon />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Delete Customer">
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={() => handleDeleteClick(customer)}
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    component="div"
                    count={pagination.total}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    labelDisplayedRows={({ from, to, count }) => 
                        `${pagination.from || from}-${pagination.to || to} of ${pagination.total || count}`
                    }
                />
            </TableContainer>

            {/* Customer Form Modal */}
            <Modal
                open={isModalVisible}
                onClose={handleModalClose}
                aria-labelledby="customer-modal-title"
            >
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '90%',
                    maxWidth: 800,
                    bgcolor: 'background.paper',
                    borderRadius: 2,
                    boxShadow: 24,
                    p: 4,
                    maxHeight: '90vh',
                    overflow: 'auto'
                }}>
                    <Typography id="customer-modal-title" variant="h6" component="h2" sx={{ mb: 2 }}>
                        {viewMode ? 'View' : editingCustomer ? 'Edit' : 'Add'} Customer
                    </Typography>
                    <CustomerForm
                        customer={editingCustomer}
                        onSuccess={handleFormSuccess}
                        onCancel={handleModalClose}
                        viewMode={viewMode}
                    />
                </Box>
            </Modal>

            <TariffDialog
                open={Boolean(tariffCustomer)}
                onClose={() => setTariffCustomer(null)}
                title={`Tariff: ${tariffCustomer?.cust_name || ''}`}
                subtitle={tariffCustomer?.cust_code}
                filename={`sunex-tariff-${(tariffCustomer?.cust_code || 'customer').replace(/\s+/g, '-').toLowerCase()}.pdf`}
                emptyMessage="No packages are priced for this customer yet."
                fetcher={async () => {
                    const response = await customerService.getTariff(tariffCustomer.id);
                    return response.success ? response.data.packages : [];
                }}
            />

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                aria-labelledby="delete-dialog-title"
            >
                <DialogTitle id="delete-dialog-title">
                    Delete Customer
                </DialogTitle>
                <DialogContent>
                    <Typography paragraph>
                        Are you sure you want to delete the customer "{customerToDelete?.cust_name}"?
                    </Typography>
                    {customerToDelete?.discounts?.length > 0 && (
                        <Typography color="warning.main">
                            This customer has {customerToDelete.discounts.length} assigned package{customerToDelete.discounts.length === 1 ? '' : 's'}.
                            Deleting the customer will also remove {customerToDelete.discounts.length === 1 ? 'that assignment' : 'those assignments'}.
                        </Typography>
                    )}
                    <Typography color="error" sx={{ mt: 1 }}>
                        This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>
                        Cancel
                    </Button>
                    <Button onClick={handleDeleteConfirm} color="error" variant="contained">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default CustomerList; 